import React, { useEffect, useState } from 'react'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageAdminKpiRecords from './useManageAdminKpiRecords';
import AdminService from '../../setting/admin/AdminService';
import KpiListService from '../../setting/kpiList/KpiListService';
import ImageUploadModal from '../../../utils/ImageUploadModal';
import AdminKpiRecordService from './AdminKpiRecordService';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '500px',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const AdminKpiRecord = () => {
  const [mode, setMode] = useState('register')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [kpiRecord, setKpiRecord] = useState({
    id: '',
    adminId: '',
    kpiId: '',
    pointsDeducted: '',
    isOverride: false,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    evidenceImageUrl: '',
    notes: ''
  });
  const [admins, setAdmins] = useState([]);
  const [kpiLists, setKpiLists] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);
  
  // Image upload states
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [evidenceImageFile, setEvidenceImageFile] = useState(null);
  
  // Filter states
  const [filterAdminId, setFilterAdminId] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const {
    kpiRecords,
    isLoading,
    error,
    addKpiRecord,
    editKpiRecord,
    deleteKpiRecord,
    refreshKpiRecords
  } = useManageAdminKpiRecords();

  // Load admins and KPI lists
  useEffect(() => {
    const loadData = async () => {
      try {
        const [adminsRes, kpiListsRes] = await Promise.all([
          AdminService.getAdmins(),
          KpiListService.getKpiLists()
        ]);
        setAdmins(adminsRes.data);
        // Filter only active KPIs
        setKpiLists(kpiListsRes.data.filter(kpi => kpi.isActive));
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAdminId, filterMonth, filterYear]);

  // Update points when KPI is selected (only if not manually changed)
  useEffect(() => {
    if (kpiRecord.kpiId) {
      const selected = kpiLists.find(k => k.id === parseInt(kpiRecord.kpiId));
      if (selected) {
        setSelectedKpi(selected);
        // Only auto-fill if points are empty or match the previous default
        if (!kpiRecord.pointsDeducted || kpiRecord.pointsDeducted === selected.deductPoint) {
          setKpiRecord(prev => ({
            ...prev,
            pointsDeducted: selected.deductPoint
          }));
        }
      }
    }
  }, [kpiRecord.kpiId, kpiLists]);

  // Auto-detect override by comparing points with default
  useEffect(() => {
    if (selectedKpi && kpiRecord.pointsDeducted) {
      const isOverride = parseFloat(kpiRecord.pointsDeducted) !== parseFloat(selectedKpi.deductPoint);
      setKpiRecord(prev => ({
        ...prev,
        isOverride: isOverride
      }));
    }
  }, [kpiRecord.pointsDeducted, selectedKpi]);

  // Filter and paginate records
  const filteredRecords = kpiRecords.filter(record => {
    if (filterAdminId && record.adminId !== parseInt(filterAdminId)) return false;
    if (filterMonth && record.month !== parseInt(filterMonth)) return false;
    if (filterYear && record.year !== parseInt(filterYear)) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = 0; i < 6; i++) {
    yearOptions.push(currentYear - i);
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setKpiRecord(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? e.target.checked : value
    }));
  };

  const handleImageUploadConfirm = (files) => {
    if (files.length > 0) {
      setEvidenceImageFile(files[0]);
      // Clear existing URL when new file is selected
      setKpiRecord(prev => ({
        ...prev,
        evidenceImageUrl: '' // Clear URL since we have a file
      }));
      toast.success('Image selected! It will be uploaded when you save the record.');
    }
    setShowImageUploadModal(false);
  };

  const handleEdit = (id) => {
    const recordToEdit = kpiRecords.find(r => r.id === id);
    if (recordToEdit) {
      setKpiRecord({
        id: recordToEdit.id,
        adminId: recordToEdit.adminId,
        kpiId: recordToEdit.kpiId,
        pointsDeducted: recordToEdit.pointsDeducted,
        isOverride: recordToEdit.isOverride,
        month: recordToEdit.month,
        year: recordToEdit.year,
        evidenceImageUrl: recordToEdit.evidenceImageUrl || '',
        notes: recordToEdit.notes || ''
      });
      setMode('edit');
      setEvidenceImageFile(null); // Reset file when editing
    }
  };

  const handleResetForm = () => {
    const currentDate = new Date();
    setKpiRecord({
      id: '',
      adminId: '',
      kpiId: '',
      pointsDeducted: '',
      isOverride: false,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
      evidenceImageUrl: '',
      notes: ''
    });
    setMode('register');
    setSelectedKpi(null);
    setEvidenceImageFile(null);
  };

  const openConfirm = async (id) => {
    const recordToDelete = kpiRecords.find(r => r.id === id);
    if (recordToDelete) {
      setKpiRecord(recordToDelete);
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create FormData to send both data and file
      const formData = new FormData();
      formData.append('adminId', kpiRecord.adminId);
      formData.append('kpiId', kpiRecord.kpiId);
      formData.append('pointsDeducted', kpiRecord.pointsDeducted);
      formData.append('isOverride', kpiRecord.isOverride);
      formData.append('month', kpiRecord.month);
      formData.append('year', kpiRecord.year);
      formData.append('notes', kpiRecord.notes || '');
      
      // Only append evidenceImageUrl if no file is being uploaded
      if (!evidenceImageFile && kpiRecord.evidenceImageUrl) {
        formData.append('evidenceImageUrl', kpiRecord.evidenceImageUrl);
      }
      
      // Append file if exists
      if (evidenceImageFile) {
        formData.append('images', evidenceImageFile);
      }

      if (mode === 'edit') {
        await editKpiRecord(kpiRecord.id, formData);
        toast.success('KPI Record updated successfully!');
        handleResetForm();
      } else {
        const returnResult = await addKpiRecord(formData);
        if (returnResult) {
          toast.success('KPI Record created successfully!');
          handleResetForm();
        } else {
          toast.error(error);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteKpiRecord = async (id) => {
    try {
      await deleteKpiRecord(id);
      toast.success('KPI Record deleted successfully!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    refreshKpiRecords();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 h-full">
      <div className="mt-4">
        <h2 className='text-5xl mb-4'>KPI Records</h2>
        
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Side - Records List */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Admin
                  </label>
                  <select
                    value={filterAdminId}
                    onChange={(e) => setFilterAdminId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Admins</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.nickname || `Admin #${admin.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Month
                  </label>
                  <select
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Months</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Year
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Years</option>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Records Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        No
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        KPI Topic
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Points
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Month/Year
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan="7" className="py-4 text-center text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    ) : currentRecords.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-4 text-center text-gray-500">
                          No records found
                        </td>
                      </tr>
                    ) : (
                      currentRecords.map((record, index) => {
                        const { id, admin, kpi, pointsDeducted, month, year, creator } = record;
                        return (
                          <tr key={id} className="hover:bg-gray-50">
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                              {indexOfFirstItem + index + 1}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                              {admin?.nickname || `Admin #${admin?.id}`}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">
                              {kpi?.topic || kpi?.group || 'N/A'}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                              {pointsDeducted}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                              {month}/{year}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                              {creator?.nickname || 'N/A'}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <PencilIcon 
                                  onClick={() => handleEdit(id)} 
                                  className='cursor-pointer w-5 h-5 text-green-600 hover:text-green-800'
                                />
                                <TrashIcon 
                                  onClick={() => openConfirm(id)} 
                                  className='cursor-pointer w-5 h-5 text-red-600 hover:text-red-800'
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(indexOfLastItem, filteredRecords.length)}</span> of{' '}
                        <span className="font-medium">{filteredRecords.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(totalPages)].map((_, idx) => {
                          const page = idx + 1;
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 2 && page <= currentPage + 2)
                          ) {
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === page
                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          } else if (page === currentPage - 3 || page === currentPage + 3) {
                            return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Input Form */}
          <div className="lg:col-span-1">
            <div className={`bg-white rounded-lg shadow p-6 sticky top-4 border-2 ${mode === 'edit' ? 'border-yellow-400' : 'border-green-400'}`}>
              <div className={`mb-4 p-3 rounded-md ${mode === 'edit' ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center space-x-2">
                  {mode === 'edit' ? (
                    <>
                      <PencilIcon className="w-5 h-5 text-yellow-600" />
                      <h3 className="text-2xl font-bold text-yellow-800">
                        Edit KPI Record
                      </h3>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl">➕</span>
                      <h3 className="text-2xl font-bold text-green-800">
                        Record New KPI
                      </h3>
                    </>
                  )}
                </div>
                <p className={`text-sm mt-1 ${mode === 'edit' ? 'text-yellow-700' : 'text-green-700'}`}>
                  {mode === 'edit' ? 'Editing existing record' : 'Creating a new KPI record'}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin *
                  </label>
                  <select
                    name="adminId"
                    value={kpiRecord.adminId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Admin</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.nickname || `Admin #${admin.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    KPI *
                  </label>
                  <select
                    name="kpiId"
                    value={kpiRecord.kpiId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select KPI</option>
                    {kpiLists.map(kpi => (
                      <option key={kpi.id} value={kpi.id}>
                        {kpi.group} - {kpi.topic} ({kpi.deductPoint} pts)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points Deducted *
                  </label>
                  <input
                    type="number"
                    name="pointsDeducted"
                    value={kpiRecord.pointsDeducted}
                    onChange={handleInputChange}
                    step="0.5"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isOverride"
                    checked={kpiRecord.isOverride}
                    disabled
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded opacity-60 cursor-not-allowed"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Override Default Points {kpiRecord.isOverride ? '(Modified)' : '(Default)'}
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month *
                  </label>
                  <input
                    type="number"
                    name="month"
                    value={kpiRecord.month}
                    onChange={handleInputChange}
                    min="1"
                    max="12"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year *
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={kpiRecord.year}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Evidence Image
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="evidenceImageUrl"
                      value={kpiRecord.evidenceImageUrl}
                      onChange={handleInputChange}
                      placeholder="No image uploaded"
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowImageUploadModal(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {evidenceImageFile ? 'Change Image' : 'Upload'}
                    </button>
                    {(kpiRecord.evidenceImageUrl || evidenceImageFile) && (
                      <button
                        type="button"
                        onClick={() => {
                          setKpiRecord(prev => ({ ...prev, evidenceImageUrl: '' }));
                          setEvidenceImageFile(null);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {(kpiRecord.evidenceImageUrl || evidenceImageFile) && (
                    <div className="mt-2">
                      <img
                        src={evidenceImageFile ? URL.createObjectURL(evidenceImageFile) : (kpiRecord.evidenceImageUrl || '')}
                        alt="Evidence"
                        className="max-w-full h-auto rounded-md border border-gray-300"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={kpiRecord.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {mode === 'edit' ? 'Update' : 'Create'}
                  </button>
                  {mode === 'edit' && (
                    <button
                      type="button"
                      onClick={handleResetForm}
                      className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          items={[]} 
          data={kpiRecord} 
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDeleteKpiRecord(kpiRecord.id)}
          titleText="Are you sure you want to delete this KPI record?"
        />
      </Modal>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageUploadModal}
        onRequestClose={() => setShowImageUploadModal(false)}
        onConfirm={handleImageUploadConfirm}
        title="Upload Evidence Image"
        maxFiles={1}
      />
    </div>
  );
};

export default AdminKpiRecord;
